import { AccountDetailsModal } from 'components/accounts/AccountDetailsModal'
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  LabelValueList,
  PencilAltIcon,
  Typography,
  useModal,
  useRouter,
} from '@forgeglobal/design-framework'
import { OwnerChip } from 'components/shared/chips/OwnerChip'
import { TransactionHistoryRollup } from 'components/shared/TransactionHistoryRollup'
import { useClientDetailsDataQuery } from 'graphql/bindings/AdminBindings'
import React, { Fragment } from 'react'
import RightAlign from 'components/shared/RightAlign'
import capitalize from 'lodash/capitalize'
import moment from 'moment'

export const AccountDetails: React.FC = () => {
  const { match } = useRouter<{ id: string }>()
  const { data } = useClientDetailsDataQuery({ variables: { id: match.params.id } })
  const account = data?.account
  const [firstOwner, ...otherOwners] = account?.owners ?? []
  const hasInfluenceWith = account?.has_influence_with ?? []

  const [showModal, hideModal] = useModal(() => {
    return <AccountDetailsModal hideModal={hideModal} accountId={match.params.id} />
  }, [match.params.id])
  const scheduleOpt = account?.outreach_schedule
  const schedule = scheduleOpt ? moment.duration(scheduleOpt) : undefined
  return (
    <Card>
      <CardHeader
        title="Account Context"
        action={
          <IconButton data-cypress="edit-account-details-button" onClick={showModal}>
            <PencilAltIcon />
          </IconButton>
        }
      />
      <CardContent>
        <LabelValueList
          items={[
            {
              label: 'Owner',
              value: <OwnerChip owner={firstOwner} />,
              isEmpty: !firstOwner,
            },
            {
              label: 'Additional Forge Contact(s)',
              value: (
                <RightAlign>
                  {otherOwners.map(owner => (
                    <OwnerChip key={owner.id} owner={owner} />
                  ))}
                </RightAlign>
              ),
              isEmpty: !otherOwners.length,
            },
            {
              label: 'Relationships strength',
              value: capitalize(account?.relationship_strength ?? ''),
              isEmpty: !account?.relationship_strength,
            },
            {
              label: 'Relationship sensitivity',
              value: (
                <Fragment>
                  <Typography variant="body1" align="right">
                    {capitalize(account?.relationship_sensitivity ?? '')}
                  </Typography>
                  <Typography variant="body1" align="right">
                    {account?.relationship_notes ?? ''}
                  </Typography>
                </Fragment>
              ),
              isEmpty: !account?.relationship_sensitivity,
            },
            {
              label: 'Business development tier',
              value: account?.business_development_tier?.toString(),
              isEmpty: !account?.business_development_tier,
            },
            {
              label: 'Outreach schedule',
              value: schedule ? `At least once every ${schedule && schedule.asDays()} days` : "Don't remind me",
            },
            {
              label: 'Has corporate influence with issuer(s)',
              value: (
                <RightAlign>
                  {hasInfluenceWith.map(comp => (
                    <Chip label={comp.company_name} variant="outlined" key={comp.company_id} />
                  ))}
                </RightAlign>
              ),
              isEmpty: !hasInfluenceWith.length,
            },
            {
              label: 'Transaction history',
              value: <TransactionHistoryRollup deals={account?.deals ?? []} />,
            },
            {
              label: 'Additional information',
              value: account?.notes ?? '',
              isEmpty: !account?.notes,
              fullWidth: true,
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}
